import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { open_snack_ac } from '../../actions/snack.ac.js';

/**
 * ITAdmin oversight for reversible DocFlow cleanup (DF-557 / INC0119984,
 * extended by DF-585 / INC0141183). Two tombstone families, same lifecycle:
 *
 *   ink.idn_deleted — soft-deleted patient shells
 *   ink.wu_deleted  — empty work units detached from a shell
 *
 * Both list days remaining until automatic hard-delete and can be restored by an
 * it-admin within the retention window. Detached work units are shown alongside
 * the shells on purpose: a shell is usually emptied by detaching its work units
 * first, so restoring the shell alone would bring it back looking emptier than
 * it was. Access is gated both here (Access Denied) and on every endpoint.
 */
class DeletedShellsLanding extends React.Component {
  state = {
    rows: [],
    loading: true,
    err: null,
    restoringId: null,
    wuRows: [],
    wuLoading: true,
    wuErr: null,
    wuRestoringId: null,
  };

  componentDidMount() {
    this.load();
    this.loadWorkunits();
  }

  load = () => {
    this.setState({ loading: true });
    window.sch
      .post('/api/idn/list_deleted_shells', {})
      .then((res) => {
        this.setState({
          rows: _.get(res, 'pkg.records', []),
          loading: false,
          err: null,
        });
      })
      .catch((err) => {
        this.setState({ loading: false, err: err.message || 'load failed' });
      });
  };

  loadWorkunits = () => {
    this.setState({ wuLoading: true });
    window.sch
      .post('/api/idn/list_detached_workunits', {})
      .then((res) => {
        this.setState({
          wuRows: _.get(res, 'pkg.records', []),
          wuLoading: false,
          wuErr: null,
        });
      })
      .catch((err) => {
        this.setState({
          wuLoading: false,
          wuErr: err.message || 'load failed',
        });
      });
  };

  restoreWorkunit = (row) => {
    if (this.state.wuRestoringId) return;
    this.setState({ wuRestoringId: row._id });
    window.sch
      .post('/api/idn/restore_workunit', {
        wu_type: row.wu_type,
        wu_id: row.wu_id,
      })
      .then(() => {
        this.props.open_snack_ac({
          message: 'Work unit restored to its patient.',
          variant: 'success',
        });
        this.setState({ wuRestoringId: null });
        this.loadWorkunits();
      })
      .catch((err) => {
        this.props.open_snack_ac({
          message: 'Restore failed: ' + (err.message || 'contact IT'),
          variant: 'error',
        });
        this.setState({ wuRestoringId: null });
      });
  };

  restore = (id) => {
    if (this.state.restoringId) return;
    this.setState({ restoringId: id });
    window.sch
      .post('/api/idn/restore_shell', { _id: id })
      .then(() => {
        const row = this.state.rows.find((r) => r._id === id);
        const detached = _.get(row, 'detached_workunits', 0);
        this.props.open_snack_ac({
          message:
            'Shell restored.' +
            (detached
              ? ` ${detached} work unit(s) were detached from it and are NOT restored — restore them from Detached Work Units below.`
              : ''),
          variant: 'success',
        });
        this.setState({ restoringId: null });
        this.load();
        this.loadWorkunits();
      })
      .catch((err) => {
        this.props.open_snack_ac({
          message: 'Restore failed: ' + (err.message || 'contact IT'),
          variant: 'error',
        });
        this.setState({ restoringId: null });
      });
  };

  render() {
    const { me } = this.props;
    const roles = _.get(me, 'user.roles', []) || [];
    if (!roles.includes('it-admin')) {
      return (
        <div style={{ padding: 40 }}>
          <Typography variant="h5">Access Denied</Typography>
          <Typography variant="body1">
            The Deleted Shells admin view requires the it-admin role.
          </Typography>
        </div>
      );
    }

    const {
      rows,
      loading,
      err,
      restoringId,
      wuRows,
      wuLoading,
      wuErr,
      wuRestoringId,
    } = this.state;

    return (
      <div style={{ padding: 24 }}>
        <Typography variant="h5" gutterBottom>
          Deleted Shells{' '}
          <Button size="small" onClick={this.load} style={{ marginLeft: 8 }}>
            Refresh
          </Button>
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Soft-deleted DocFlow patient shells. Each is permanently removed
          automatically once its retention window elapses; restore returns it to
          live search.
        </Typography>

        {loading && <CircularProgress size={28} style={{ marginTop: 24 }} />}
        {err && (
          <Typography color="error" style={{ marginTop: 16 }}>
            {err}
          </Typography>
        )}

        {!loading && !err && rows.length === 0 && (
          <Typography style={{ marginTop: 24 }}>
            No soft-deleted shells.
          </Typography>
        )}

        {!loading && !err && rows.length > 0 && (
          <Paper style={{ marginTop: 16, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Shell (patient)</TableCell>
                  <TableCell align="right">Detached WUs</TableCell>
                  <TableCell>Deleted by</TableCell>
                  <TableCell>Deleted at</TableCell>
                  <TableCell align="right">Days left</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r._id}>
                    <TableCell>{r._id}</TableCell>
                    <TableCell align="right">
                      {r.detached_workunits ? (
                        <span
                          title="Work units detached from this shell. Restoring the shell does not restore them — do that from the table below."
                          style={{ color: '#b71c1c', fontWeight: 700 }}
                        >
                          {r.detached_workunits}
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{r.deleted_by}</TableCell>
                    <TableCell>
                      {r.deleted_at
                        ? new Date(r.deleted_at).toLocaleString()
                        : ''}
                    </TableCell>
                    <TableCell align="right">
                      {_.isNil(r.days_remaining) ? '—' : r.days_remaining}
                    </TableCell>
                    <TableCell>{r.reason || ''}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={restoringId === r._id}
                        onClick={() => this.restore(r._id)}
                      >
                        {restoringId === r._id ? (
                          <CircularProgress size={16} />
                        ) : (
                          'Restore'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        <Typography variant="h5" gutterBottom style={{ marginTop: 40 }}>
          Detached Work Units{' '}
          <Button
            size="small"
            onClick={this.loadWorkunits}
            style={{ marginLeft: 8 }}
          >
            Refresh
          </Button>
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Work units that held no documents and were detached from a patient so
          the shell could be removed. Each is permanently removed automatically
          once its retention window elapses; restore returns it to its patient.
        </Typography>

        {wuLoading && <CircularProgress size={28} style={{ marginTop: 24 }} />}
        {wuErr && (
          <Typography color="error" style={{ marginTop: 16 }}>
            {wuErr}
          </Typography>
        )}

        {!wuLoading && !wuErr && wuRows.length === 0 && (
          <Typography style={{ marginTop: 24 }}>
            No detached work units.
          </Typography>
        )}

        {!wuLoading && !wuErr && wuRows.length > 0 && (
          <Paper style={{ marginTop: 16, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Work unit</TableCell>
                  <TableCell>Shell (patient)</TableCell>
                  <TableCell>Detached by</TableCell>
                  <TableCell>Detached at</TableCell>
                  <TableCell align="right">Days left</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wuRows.map((r) => (
                  <TableRow key={r._id}>
                    <TableCell style={{ fontFamily: 'monospace' }}>
                      {r.wu_type}&nbsp;{String(r.wu_id)}
                    </TableCell>
                    <TableCell>{r.idn}</TableCell>
                    <TableCell>{r.detached_by}</TableCell>
                    <TableCell>
                      {r.detached_at
                        ? new Date(r.detached_at).toLocaleString()
                        : ''}
                    </TableCell>
                    <TableCell align="right">
                      {_.isNil(r.days_remaining) ? '—' : r.days_remaining}
                    </TableCell>
                    <TableCell>{r.reason || ''}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={wuRestoringId === r._id}
                        onClick={() => this.restoreWorkunit(r)}
                      >
                        {wuRestoringId === r._id ? (
                          <CircularProgress size={16} />
                        ) : (
                          'Restore'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </div>
    );
  }
}

const mapStateToProps = (stateFromStore) => ({
  me: stateFromStore.me,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ open_snack_ac }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(DeletedShellsLanding);
